import { amountDue, type InvoiceEnrollmentRef } from '@repo/shared-types';

/**
 * The manual-send message template a teacher copies into Zalo — see
 * docs/superpowers/specs/2026-08-17-manual-send-template-design.md §2.
 * Used whenever `OrganizationSettings.invoiceMessageTemplate` is null; also
 * pre-filled into the (separate) settings editor. Verbatim from the spec —
 * do not reformat, the line breaks are part of the message.
 */
export const DEFAULT_INVOICE_MESSAGE_TEMPLATE = `Chào {phu_huynh},

Thầy xin phép gửi học phí {ky} cho {hoc_vien}. Tổng cộng là {tong_tien}.

{link}

Quý phụ huynh/học viên vui lòng click vào link hóa đơn phía trên để quét mã QR thanh toán trễ nhất trước ngày {han_thanh_toan}. Vui lòng giữ nguyên nội dung chuyển khoản và số tiền để hóa đơn được đối soát tự động. Thầy xin cảm ơn!`;

/**
 * Plain-string inputs `resolveTemplate` needs to fill in the six tokens.
 * Deliberately free of any API/`Invoice` type — every field here is already
 * the exact string (or list of strings) to substitute, so the resolver is
 * pure and trivially testable without a fixture invoice. `buildInvoiceMessageData`
 * below is the (Invoice-aware) adapter that produces this shape.
 */
export interface InvoiceMessageData {
  /** {phu_huynh} — payer/parent display name. */
  payerName: string;
  /** {ky} — billing-period label, e.g. "tháng 8/2026" or "Khóa 3". */
  periodLabel: string;
  /** {hoc_vien} entries, pre-formatted as "Tên (Môn)"; joined with ", ".
   *  Empty falls back to `invoiceTitleLine` (spec: "empty student list →
   *  the invoice's own title line"). */
  students: string[];
  /** Fallback for {hoc_vien} when `students` is empty. */
  invoiceTitleLine: string;
  /** {tong_tien} — formatted total, e.g. "1.500.000đ". */
  totalFormatted: string;
  /** {link} — the public invoice URL. */
  link: string;
  /** {han_thanh_toan} — "dd/MM/yyyy", or null when the invoice has no due
   *  date (spec fallback: "sớm nhất có thể"). */
  dueDateFormatted: string | null;
}

const DUE_DATE_FALLBACK = 'sớm nhất có thể';

/**
 * What the settings editor (BillingRulesSection) should send in the
 * `invoiceMessageTemplate` field of a `PUT /org/settings` partial update,
 * given the editor's current text and the value the org had stored before
 * this edit — see docs/superpowers/specs/2026-08-17-manual-send-template-design.md §3.
 *
 * Compares `text.trim()` against the default, not the raw textarea value:
 * the API schema (`updateOrgSettingsSchema`) trims before storing, so
 * comparing untrimmed would let a teacher who retypes the default with a
 * stray trailing space/newline fall into the "custom text" branch below —
 * the server then trims it down to a byte-identical copy of the default,
 * silently freezing the org on today's snapshot instead of tracking future
 * changes to `DEFAULT_INVOICE_MESSAGE_TEMPLATE` via the null fallback.
 *
 * A trimmed-empty textarea (teacher deleted everything) is treated exactly
 * like the default, not stored as `''`: an empty stored template would make
 * `resolveTemplate` — and therefore every "Sao chép nội dung" — hand back a
 * blank message forever. Clearing the editor and saving means "revert to the
 * default", never "send nothing".
 *
 * - `text.trim()` is empty OR equals the (trimmed) default, AND nothing was
 *   stored before (`storedTemplate === null`): returns `undefined` — the
 *   caller must OMIT the field from the payload rather than send a redundant
 *   literal copy of the default (the route already treats "omitted" and
 *   "null" as "use the default" the same way `resolveTemplate`'s caller
 *   does).
 * - Same text condition, but something WAS stored (the teacher edited a
 *   custom template back to exactly the default, or emptied it out):
 *   returns explicit `null` — clears the stored override, so a future
 *   change to `DEFAULT_INVOICE_MESSAGE_TEMPLATE` isn't silently masked by a
 *   stale literal copy of today's default sitting in the database.
 * - Any other text: returns it trimmed (matching what the server will
 *   actually persist), to be stored verbatim.
 */
export function resolveTemplateSaveValue(
  text: string,
  storedTemplate: string | null,
): string | null | undefined {
  const trimmed = text.trim();
  const isDefaultOrEmpty = trimmed === '' || trimmed === DEFAULT_INVOICE_MESSAGE_TEMPLATE.trim();
  if (isDefaultOrEmpty) return storedTemplate === null ? undefined : null;
  return trimmed;
}

/** Resolve every occurrence of a known token; unknown `{tokens}` pass through
 *  literally so a stray brace in a teacher-edited template never crashes. */
export function resolveTemplate(template: string, data: InvoiceMessageData): string {
  const values: Record<string, string> = {
    phu_huynh: data.payerName,
    ky: data.periodLabel,
    hoc_vien: data.students.length > 0 ? data.students.join(', ') : data.invoiceTitleLine,
    tong_tien: data.totalFormatted,
    link: data.link,
    han_thanh_toan: data.dueDateFormatted ?? DUE_DATE_FALLBACK,
  };

  return template.replace(/\{([a-zA-Z_]+)\}/g, (match, key: string) => {
    const value = values[key];
    return value !== undefined ? value : match;
  });
}

/** Amount formatted the way the rest of the invoice UI already does
 *  (see CloseMonthPage's `vnd` helper) — "1.500.000đ", not the `Intl`
 *  currency formatter's "1.500.000 ₫" used on the invoice detail page. */
function formatTotal(amount: number): string {
  return `${Number(amount).toLocaleString('vi-VN')}đ`;
}

/** "dd/MM/yyyy" from an ISO date string, in UTC — invoice due dates are
 *  stored as bare calendar dates (midnight UTC), so UTC getters keep the day
 *  stable regardless of the machine's local timezone (unlike `toLocaleDateString`,
 *  which the invoice detail page uses but which is not deterministic in tests). */
function formatDueDate(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${d.getUTCFullYear()}`;
}

/** "tháng M/YYYY" (no leading zero on the month) from an ISO date string,
 *  in UTC — same rationale as `formatDueDate`. */
function monthLabelOf(iso: string): string {
  const d = new Date(iso);
  return `tháng ${d.getUTCMonth() + 1}/${d.getUTCFullYear()}`;
}

/** One invoice item as the adapter needs it — description (for the
 *  "Học phí"-primary disambiguation below) plus its enrollment ref.
 *  `enrollmentRef` is optional rather than a plain `| null` union because the
 *  two real callers disagree on whether the key exists at all: `GET
 *  /invoices/:id` (`InvoiceDetail`, InvoiceDetailPage → SendInvoiceDialog)
 *  always includes it, `null` on manual lines and adjustment lines whose
 *  period-slot was already claimed by a sibling primary line; `GET
 *  /invoices/by-artifact` (the chat ArtifactPanel's data source, still typed
 *  as the base `Invoice`) omits the field from its response entirely, so at
 *  runtime it's `undefined` there — chat-created invoices are never
 *  enrollment-linked today. Both are read through `item.enrollmentRef ??
 *  null` below so the two shapes behave identically rather than the
 *  by-artifact path silently taking a different code path. */
interface InvoiceMessageItem {
  description: string;
  enrollmentRef?: InvoiceEnrollmentRef | null;
}

/** Minimal invoice shape `buildInvoiceMessageData` needs. Both `Invoice`
 *  (the by-artifact hook's type) and `InvoiceDetail` (the detail hook's type,
 *  see useInvoices.ts) satisfy this structurally, so SendInvoiceDialog's
 *  `invoice: Invoice` prop — the one call site — never needs to widen: an
 *  `InvoiceDetail` value is itself assignable to `Invoice` (its items are a
 *  strict superset of `InvoiceItem`, everything else matches). */
interface InvoiceMessageSource {
  paidAmount?: number;
  items: InvoiceMessageItem[];
  client?: { name?: string | null } | null;
  totalAmount: number;
  dueDate: string | null;
  invoiceCode: string;
  createdAt: string;
}

/** "YYYY-MM" → "tháng M/YYYY" (no leading zero on the month); any other shape
 *  ("Khóa N") passes through unchanged. */
function formatPeriodLabel(raw: string): string {
  const monthly = raw.match(/^(\d{4})-(\d{2})$/);
  if (!monthly?.[1] || !monthly[2]) return raw;
  return `tháng ${Number(monthly[2])}/${monthly[1]}`;
}

/**
 * Maps the dashboard's invoice payload into the plain-string shape
 * `resolveTemplate` needs. Kept in this file (rather than as its own adapter
 * module) since it is the one and only producer of `InvoiceMessageData` in
 * the app.
 *
 * THI-88: reads each item's structured `enrollmentRef` instead of
 * regex-parsing the free-text `description` the old parser needed (deleted —
 * see git history for `parseStudentSubject`/`parsePeriodLabel` if curious).
 * Students/period are derived only from items that carry a ref: a period's
 * primary ("Học phí") line and its "Bù trừ" adjustment never both carry one
 * (only the primary does when both exist on the same period; the adjustment
 * gets it only when it's the sole line for that period) — see
 * `InvoiceEnrollmentRef`'s doc comment in `@repo/shared-types` for the full
 * rule.
 */
export function buildInvoiceMessageData(invoice: InvoiceMessageSource, publicUrl: string): InvoiceMessageData {
  const refItems = invoice.items
    .map((item) => ({ item, ref: item.enrollmentRef ?? null }))
    .filter((x): x is { item: InvoiceMessageItem; ref: InvoiceEnrollmentRef } => x.ref !== null);

  // Dedupe (two items can resolve to the same "student (subject)" pair —
  // e.g. a sole-claimant adjustment line for the same enrollment a primary
  // line elsewhere already named) while keeping first-seen order.
  const students = Array.from(
    new Set(refItems.map(({ ref }) => `${ref.studentName} (${ref.subjectName})`)),
  );

  // Period label: when the invoice's refs span more than one distinct
  // period (a current-month primary plus a prior-month adjustment that
  // happened to be the sole claimant of its own period's ref), prefer the
  // "Học phí" primary line's label — that is the period the invoice is
  // actually "for" — over the adjustment's. Falls back to the first ref when
  // none look like a primary line, and to the invoice's own creation month
  // when there are no refs at all (manual invoice: {hoc_vien} falls back to
  // `invoiceTitleLine` via resolveTemplate, so {ky} just needs *a* month,
  // not one derived from billing data that doesn't exist).
  const primaryRef = refItems.find(({ item }) => item.description.includes('Học phí'))?.ref;
  const chosenRef = primaryRef ?? refItems[0]?.ref;
  const periodLabel = chosenRef ? formatPeriodLabel(chosenRef.periodLabel) : monthLabelOf(invoice.createdAt);

  return {
    payerName: invoice.client?.name ?? '',
    periodLabel,
    students,
    invoiceTitleLine: `Hoá đơn ${invoice.invoiceCode}`,
    // What is left: the same number the QR beside this message encodes (§1.9).
    totalFormatted: formatTotal(amountDue(invoice.totalAmount, invoice.paidAmount ?? 0)),
    link: publicUrl,
    dueDateFormatted: invoice.dueDate ? formatDueDate(invoice.dueDate) : null,
  };
}
